import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { Box, Grid, MenuItem, Select, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { ReportNames, useGetLeaderboardConfigsQuery, useLeaderboardOverviewStatsQuery } from 'api';
import { DatePickers, Loaders, PaddedPaper } from 'components';
import { useProtectedClient, useDateRange, useNotify } from 'hooks';

import {
  DailyLeaderboardStatsChart,
  LeaderboardStatTable,
  TotalPointStatsChart,
} from '../components/Analytics';
import { DownloadReportButton, RangePickerWrapper } from '../components/shared';

const LeaderboardsDashboard: FC = () => {
  const client = useProtectedClient();
  const notify = useNotify();
  const { t } = useTranslation();

  const { startDate, setStartDate, endDate, setEndDate, dateRange, setDateRange } = useDateRange();
  const [openLeaderboard, setOpenLeaderboard] = useState(false);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState('');
  const [requestReport, setRequestReport] = useState(false);
  const { data } = useGetLeaderboardConfigsQuery();
  const hasData = !!data?.getLeaderboardConfigs;

  useLeaderboardOverviewStatsQuery(
    {
      leaderboard_config_id: getSelectedLeaderboard(),
      client_id: client.id,
      start_date: startDate,
      end_date: endDate,
      report: ReportNames.Leaderboards,
    },
    {
      enabled: requestReport,
      onSuccess: () => notify.info({ message: t('analytics:reportRequested') }),
      onError: () => notify.error({ message: t('analytics:reportRequestedError') }),
      onSettled: () => setRequestReport(false),
    }
  );

  function getSelectedLeaderboard() {
    if (!hasData) {
      return null;
    }

    const { getLeaderboardConfigs } = data;
    return !selectedLeaderboard ? getLeaderboardConfigs[0].id : selectedLeaderboard;
  }

  function handleLeaderboardConfigSelectOpen() {
    setOpenLeaderboard(true);
  }

  function handleLeaderboardConfigSelectClose() {
    setOpenLeaderboard(false);
  }

  function handleLeaderboardConfigSelectChange(event: ChangeEvent<HTMLSelectElement>) {
    setSelectedLeaderboard(event.target.value);
  }

  function handleReportRequest() {
    setRequestReport(true);
  }

  function renderLeaderboardMenu() {
    if (!hasData) {
      return null;
    }

    const { getLeaderboardConfigs } = data;

    return (
      <Select
        open={openLeaderboard}
        onOpen={handleLeaderboardConfigSelectOpen}
        onClose={handleLeaderboardConfigSelectClose}
        value={getSelectedLeaderboard()}
        onChange={handleLeaderboardConfigSelectChange}
      >
        {getLeaderboardConfigs.map(config => (
          <MenuItem value={config.id} key={config.id}>
            {config.title}
          </MenuItem>
        ))}
      </Select>
    );
  }

  function renderCharts() {
    return (
      <Fragment>
        <Grid item>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <TotalPointStatsChart
                startDate={startDate}
                endDate={endDate}
                leaderboardId={getSelectedLeaderboard()}
              />
            </Grid>
            <Grid item xs={8}>
              <DailyLeaderboardStatsChart
                startDate={startDate}
                endDate={endDate}
                leaderboardId={getSelectedLeaderboard()}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <LeaderboardStatTable
            startDate={startDate}
            endDate={endDate}
            leaderboardId={getSelectedLeaderboard()}
          />
        </Grid>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Box mb={2}>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <Typography variant="h5" color="textSecondary">
              {t('analytics:engagement_leaderboard')}
            </Typography>
          </Grid>
          <Grid item>
            <DownloadReportButton onClick={handleReportRequest} />
          </Grid>
        </Grid>
      </Box>
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid item>
          <PaddedPaper padding={12}>
            <Grid container spacing={2}>
              <Grid item>
                <RangePickerWrapper>
                  <DatePickers.Range
                    dateRange={dateRange}
                    startDate={startDate}
                    endDate={endDate}
                    onSetStart={setStartDate}
                    onSetEnd={setEndDate}
                    onSetRange={setDateRange}
                  />
                </RangePickerWrapper>
              </Grid>
              <Grid item>{renderLeaderboardMenu()}</Grid>
            </Grid>
          </PaddedPaper>
        </Grid>
        {hasData ? renderCharts() : <Loaders.Standard />}
      </Grid>
    </Fragment>
  );
};

export default LeaderboardsDashboard;
